import React from 'react';
import Layout from "../../layout/Layout";
import {
  getDetails
} from "../../../actions/admin/quotation";
import moment from "moment";
import PageNotFound from "../../404page/PageNotFound";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min";
import jwtDecode from "jwt-decode";
import { useSelector } from "react-redux";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer"
import QuotationDocument from "./_components/pdf/QuotationDocument"
import TestDoc from "./_components/pdf/TestDoc"
import { useTranslation } from "react-i18next";
import LanguageCard from "../../../components/layout-new-theme/LanguageCard";
import { PrintIcon, DownloadNarrowIcon, CollapseUpIcon, CollapseDownIcon } from "../../icons";
import {
  getAdditionalGroups,
  getAllAdditionalItems,
  computeQuotationTotalAmount,
  computeStandaloneItemsTotal,
} from "./_utils/quotationTotals";

const tlf_logo_quotation = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/tlf-logo-quotation.png";

// Shown while the quotation detail is being fetched. Mirrors the download
// layout (header, To/From block, transfer cards, totals) using the same
// tw:animate-pulse placeholder style as the other quotation skeletons.
const QuotationDownloadSkeleton = () => (
  <div className="bg-white rounded p-3">
    <div className="bg-white rounded mb-5">
      {/* Title + logo */}
      <div className="p-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="tw:h-7 tw:w-48 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
          <div className="tw:h-10 tw:w-32 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
        </div>
      </div>
      <hr className="m-0 divider" />

      {/* To / From / meta */}
      <div className="p-3 mb-3">
        <div className="row m-0 g-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="col-md-4">
              <div className="tw:h-3 tw:w-12 tw:animate-pulse tw:bg-gray-200 tw:rounded tw:mb-2" />
              <div className="tw:h-4 tw:w-3/4 tw:animate-pulse tw:bg-gray-200 tw:rounded tw:mb-2" />
              <div className="tw:h-4 tw:w-1/2 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
            </div>
          ))}
        </div>
      </div>
      <hr className="m-0 divider" />

      {/* Transfer cards */}
      <div className="p-3">
        {[1, 2].map((i) => (
          <div key={i} className="border rounded p-3 mb-3">
            <div className="tw:h-4 tw:w-40 tw:animate-pulse tw:bg-gray-200 tw:rounded tw:mb-3" />
            <div className="row g-2 mb-3">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="col-md-3">
                  <div className="tw:h-3 tw:w-16 tw:animate-pulse tw:bg-gray-200 tw:rounded tw:mb-2" />
                  <div className="tw:h-4 tw:w-24 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-between">
              <div className="tw:h-4 tw:w-28 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
              <div className="tw:h-4 tw:w-20 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="p-3">
        <div className="d-flex justify-content-end">
          <div className="col-md-4">
            <div className="d-flex justify-content-between mb-2">
              <div className="tw:h-4 tw:w-24 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
              <div className="tw:h-4 tw:w-20 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
            </div>
            <div className="d-flex justify-content-between">
              <div className="tw:h-5 tw:w-20 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
              <div className="tw:h-5 tw:w-28 tw:animate-pulse tw:bg-gray-200 tw:rounded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const QuotationDownloadPage = () => {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.setting);
  const { lang } = settings;
  moment.locale(lang?.toUpperCase() === "EN" ? "en" : "ja");
  const history = useHistory();
  const [userInfo, setUserInfo] = useState({});

  const { quotationId } = useParams();

  const [quotationDetail, setQuotationDetail] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [isShow, setIsShow] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const [isPrint, setIsPrint] = useState(false);

  const [openItems, setOpenItems] = useState(["00"]); // State to track open items

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedJwt = jwtDecode(token);

      setUserInfo(decodedJwt.user);
    }
    if (quotationId) {
      handleFetchDetailQuotation(quotationId);
    } else {
      setError(true);
    }
  }, []);

  const checkToken = () => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedJwt = jwtDecode(token);
      setUserInfo(decodedJwt.user);
      setIsShow(true);
    } else {
      setShowModal(true);
    }
  };

  useEffect(() => {
    if (quotationDetail !== null) {
      if (quotationDetail?.user?.signUpStatus !== "QUOTATION_GUEST") {
        checkToken();
      } else {
        setIsShow(true);
      }
    }
  }, [quotationDetail]);

  const formatMomentDate = {
    "EN": "MMMM D, YYYY",
    "en": "MMMM D, YYYY",
    "JP": "YYYY年M月D日",
    "jp": "YYYY年M月D日",
  }

  console.log("lang", lang)

  const bookingItemsDetailOrder = [
    "charterCost",
    "lateNightFee",
    "discount",
    "tollFee",
    "longDistanceSurcharge",
    "driverAccomodationTotal"
  ]

  const getBookingType = (bookingType, isAirportTransfer) => {
    switch (bookingType) {
      case "byTheHour":
        return "byTheHour"
      case "specifiedDestination":
        if (isAirportTransfer) {
          return "airportTransfer"
        } else {
          return "specifiedDestination"
        }
      default:
        return "";
    }
  }

  const handleFetchDetailQuotation = async (quotationId) => {
    try {
      var res = await getDetails(quotationId);
      if (res.quotationStatus === "ACTIVE" || res.quotationStatus === "EXPIRED" || res.quotationStatus === "DRAFT") {
        setQuotationDetail(res);
      }
    } catch (error) {
      // setError(true);
    } finally {
      setLoading(false);
    }
  };


  const toggleOpen = (index) => {
    setOpenItems((prevOpenItems) => {
      if (prevOpenItems.includes(index)) {
        return prevOpenItems.filter((itemIndex) => itemIndex !== index); // Close the item
      } else {
        return [...prevOpenItems, index]; // Open the item
      }
    });
  };

  if (error) {
    return <PageNotFound />;
  }

  // Quotation detail is still being fetched — show a skeleton inside the page shell.
  if (loading) {
    return (
      <Layout>
        <main
          className="col-md-9 ms-sm-auto col-lg-10 px-md-4 py-4 w-100 tw:!font-hanken-grotesk"
          style={{ backgroundColor: "#F6F6F6" }}
        >
          <QuotationDownloadSkeleton />
        </main>
      </Layout>
    );
  }

  const getPhoneNumber = (item) => {
    if (item.role === "individual" || item.role === "business") {
      const accountDetail = item[item.role === "individual" ? "individualDetail" : "businessDetail"]
      return accountDetail.billingInformation.phone !== "" ? `(${accountDetail.billingInformation.prefix_number ?? "-"}) ${accountDetail.billingInformation.phone}` : "-";
    }
    return "";
  };

  const handlePrint = () => {
    var data = [];
    for (let index = 0; index < quotationDetail?.bookings.length; index++) {
      for (let index2 = 0; index2 < quotationDetail?.bookings[index]?.bookingItems.length; index2++) {
        data.push(index + "" + index2);
      }

    }
    setOpenItems(data);
    window.print();
  }

  const handlePrintNew = () => {
    setIsPrint(!isPrint);
  }

  return (
    <>
      <Layout>
        <main
          className="col-md-9 ms-sm-auto col-lg-10 px-md-4 py-4 w-100 tw:!font-hanken-grotesk"
          style={{ backgroundColor: "#F6F6F6" }}
        >
          <div className="d-none d-md-block no-print">
            <div className="d-flex justify-content-between mb-2 ">
              <div className="col no-print">
                <div className="d-flex gap-2 align-items-stretch">
                  <button
                    className="bg-dark rounded"
                    style={{ minHeight: "100%" }}
                    onClick={handlePrintNew}
                  >
                    <div className="tw:flex tw:gap-2 tw:px-3 tw:py-2 tw:justify-center tw:items-center">
                      <PrintIcon />
                      <p className="m-0 text-white">{isPrint ? "Back" : "Print"}</p>
                    </div>
                  </button>
                  <PDFDownloadLink document={<QuotationDocument data={quotationDetail} />} fileName={`TLF-QUOTATION-${quotationDetail?.quotationID}.pdf`} style={{ display: "flex", textDecoration: "none" }}>
                    {({ loading }) => (
                      <button className="bg-dark rounded h-100" disabled={loading}>{
                        loading ? (
                          <div className="tw:flex tw:gap-2 tw:px-3 tw:py-2 tw:justify-center tw:items-center">
                            <DownloadNarrowIcon />
                            <p className="m-0 text-white">Loading</p>
                          </div>
                        ) : (
                          <div className="tw:flex tw:gap-2 tw:px-3 tw:py-2 tw:justify-center tw:items-center">
                            <DownloadNarrowIcon />
                            <p className="m-0 text-white">Download</p>
                          </div>
                        )
                      }</button>
                    )}
                  </PDFDownloadLink>
                  <div className="col no-print">
                    <div className="d-flex justify-content-end gap-2">
                      <LanguageCard />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* MOBILE BUTTONS */}
          <div className="d-block d-md-none no-print">
            <div className="row mb-2 ">
              <div className="col-12  no-print">
                <div className="d-flex gap-2">
                  <button
                    className="bg-dark rounded"
                    onClick={handlePrint}
                  >
                    <div className="col tw:flex tw:gap-2 tw:px-3 tw:py-2 tw:justify-center tw:items-center">
                      <PrintIcon />
                      <p className="m-0 text-white">Print</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded p-3">
            {!isPrint ? (
              <div className="bg-white rounded mb-5">
                <div className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <h4
                      className='tw:!m-0 tw:!font-hanken-grotesk'
                      style={{
                        fontSize: "29px",
                        fontWeight: "900",
                        color: "#212529",
                      }}
                    >
                      {t("Quotation.LinkPage.Quotation")}
                    </h4>
                    <img src={tlf_logo_quotation} alt="" srcSet="" />
                  </div>
                </div>
                <hr className="m-0 divider" />
                <div className="p-3 mb-3">
                  <div className="row m-0">
                    <div className="col-md-4">
                      <p className="tw:!font-hanken-grotesk m-0 w-700 fs-12 text-A7A7A7">{t("Quotation.LinkPage.To.English")}</p>
                      <p
                        className="tw:!font-hanken-grotesk m-0 w-700 fs-16 text-4E4E4E"
                        style={{ fontWeight: "700 !important" }}
                      >
                        {quotationDetail?.user?.name} {" "} {t("Quotation.LinkPage.To.Japanese")}
                      </p>
                      <p className="tw:!font-hanken-grotesk text-gold text-decoration-underline">
                        {quotationDetail?.user?.email}
                      </p>
                      <p className="tw:!font-hanken-grotesk">
                        {quotationDetail?.user?.role === "individual"
                          ? quotationDetail?.user?.individualDetail
                            ?.billingInformation?.address
                          : quotationDetail?.user?.businessDetail
                            ?.billingInformation?.address}
                      </p>
                      <p className="tw:!font-hanken-grotesk">
                        {" "}
                        {quotationDetail?.user &&
                          getPhoneNumber(quotationDetail?.user)}
                      </p>
                    </div>
                    <div className="col-md-5">
                      <p className="tw:!font-hanken-grotesk m-0 w-700 fs-12 text-A7A7A7">{t("Quotation.LinkPage.From")}</p>
                      <p
                        className="tw:!font-hanken-grotesk m-0 w-700 fs-16 text-4E4E4E"
                        style={{ fontWeight: "700 !important" }}
                      >
                        {t("Quotation.LinkPage.CompanyDetail.Name")}
                      </p>
                      <p className="tw:!font-hanken-grotesk text-gold text-decoration-underline">
                        {t("Quotation.LinkPage.CompanyDetail.Email")}
                      </p>
                      <p className="tw:!font-hanken-grotesk">
                        {t("Quotation.LinkPage.CompanyDetail.Address")}
                      </p>
                      <p className="tw:!font-hanken-grotesk">{t("Quotation.LinkPage.CompanyDetail.Telp")}</p>
                    </div>
                    <div className="col-md-3">
                      <div className="d-flex ">
                        <table>
                          <tr>
                            <td className="pb-0">
                              <p className="tw:!font-hanken-grotesk p-0 m-0 text-7C7B7B fw-400 fs-14 text-nowrap">
                                {t("Quotation.LinkPage.IssueDate")}
                              </p>
                            </td>
                            <td className="pb-0">
                              <p className="tw:!font-hanken-grotesk ms-3 mb-0 text-4E4E4E fw-400 fs-14 text-nowrap fw-400">
                                {" "}
                                {quotationDetail &&
                                  moment(quotationDetail?.issueDate).format(
                                    formatMomentDate[lang]
                                  )}
                              </p>
                            </td>
                          </tr>
                          <tr>
                            <td className="pb-0">
                              <p className="tw:!font-hanken-grotesk p-0 m-0 text-7C7B7B fw-400 fs-14 text-nowrap">
                                {t("Quotation.LinkPage.QuotationNumber")}
                              </p>
                            </td>
                            <td className="pb-0">
                              <p className="tw:!font-hanken-grotesk ms-3 mb-0 text-4E4E4E fw-400 fs-14 text-nowrap  fw-400">
                                {" "}
                                {quotationDetail?.quotationID}
                              </p>
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="row m-0 mt-3 mt-md-0">
                    <div className="col-md-4">
                      <table className="w-100 border-282828">
                        <tr className="border-282828">
                          <td rowSpan={2} className="text-center border-282828">
                            <p className="tw:!font-hanken-grotesk mb-2 fs-14 fw-400">{t("Quotation.LinkPage.ValidUntil")}</p>
                            <p className="tw:!font-hanken-grotesk mb-0 fs-14 fw-700 text-282828">
                              {quotationDetail &&
                                moment(quotationDetail?.expiredDate).format(
                                  formatMomentDate[lang]
                                )}
                            </p>
                          </td>
                          <td className="text-center border-282828 bg-dark">
                            <p className="tw:!font-hanken-grotesk m-0 text-white fw-700 fs-14">{t("Quotation.LinkPage.Total")}</p>
                          </td>
                        </tr>
                        <tr className="border-282828">
                          <td className="text-center border-282828">
                            <p className="tw:!font-hanken-grotesk m-0 text-282828 fw-700 fs-20">
                              {`${computeQuotationTotalAmount(quotationDetail).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                            </p>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>

                {/* DESKTOP VIEW */}
                <div className=" d-none d-md-block">
                  <table className="table table-quotation-items mb-3">
                    <tr className="bg-dark">
                      <th className="align-middle py-2" style={{ width: "2%", whiteSpace: "nowrap" }}>
                        <p className="tw:!font-hanken-grotesk py-0 ms-2 text-white mb-0">{t("Quotation.LinkPage.Table.ServiceDate")}</p>
                      </th>
                      <th className="align-middle py-2" style={{ width: "10%", whiteSpace: "nowrap" }}>
                        <b className="tw:!font-hanken-grotesk pb-0 ms-2">{t("Quotation.LinkPage.Table.Name")}</b>
                        <p className="tw:!font-hanken-grotesk py-0 ms-2 mb-0 text-white">{t("Quotation.LinkPage.Table.Description")}</p>
                      </th>
                      <th className="align-middle py-2" style={{ width: "1%", whiteSpace: "nowrap" }}>
                        <p className="tw:!font-hanken-grotesk py-0 ms-2 text-white mb-0">{t("Quotation.LinkPage.Table.UnitPrice")}</p>
                      </th>
                      <th className="align-middle py-2" style={{ width: "1%", whiteSpace: "nowrap" }}>
                        <p className="tw:!font-hanken-grotesk py-0 ms-2 text-white mb-0">{t("Quotation.LinkPage.Table.Qty")}</p>
                      </th>
                      <th className="align-middle py-2" style={{ width: "1%", whiteSpace: "nowrap" }}>
                        <p className="tw:!font-hanken-grotesk py-0 ms-2 text-white mb-0">{t("Quotation.LinkPage.Table.Amount")}</p>
                      </th>
                    </tr>

                    <>
                      {quotationDetail?.bookings.map((item, index) => (
                        <>
                          {/* Booking Type Row */}
                          <tr className="bg-7C7B7B">
                            <td colSpan={5}>
                              <p className="tw:!font-hanken-grotesk m-0 tw:!text-[#FFFFFF] tw:!text-[14px] tw:!font-[400]">
                                {t(`BookingType.${getBookingType(item?.bookingType, item?.isAirportTransfer)}`)}
                              </p>
                            </td>
                          </tr>

                          {/* Booking Items */}
                          {item?.bookingItems.map((b, index) => (
                            <>
                              <tr
                                key={index}
                                className={`${index !== 0 ? 'border-top' : ''}`}
                              >
                                <td>
                                  <p className="tw:!font-hanken-grotesk py-0 mb-0 tw:!text-[14px] tw:!text-[#4E4E4E]">
                                    {
                                      lang === "JP" ? b?.serviceDateJP || b?.serviceDate : b?.serviceDate
                                    }
                                  </p>
                                </td>
                                <td>
                                  <p className="tw:!font-hanken-grotesk tw:!text-[#4E4E4E] tw:!font-[700] tw:!text-[14px] tw:!py-0 tw:!mb-0">
                                    {
                                      lang === "JP" ? b?.nameJP || b?.name : b?.name
                                    }
                                  </p>
                                  <p className="tw:!font-hanken-grotesk tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px] tw:!py-0 tw:!mb-0">
                                    {
                                      lang === "JP" ? b?.descJP || b?.desc : b?.desc
                                    }
                                  </p>
                                </td>
                                <td className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{`${b?.price?.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</td>
                                <td className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{b?.qty}</td>
                                <td className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{`${b?.totalPrice?.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</td>
                              </tr>

                              {b.type === "vehicle" && (
                                [...Array(bookingItemsDetailOrder.length)].map((x, i) => (
                                  item.useDiscount ? (
                                    bookingItemsDetailOrder[i] === "discount" ? (
                                      <tr key={i} className="bg-F5F5F5">
                                        <td colSpan={4}>
                                          <p className="tw:!font-hanken-grotesk px-0 py-0 mb-0 text-description text-end">{`${t("Quotation.LinkPage.Table.Discount")} (${Number(b?.discount || 0)}%)`}</p>
                                        </td>
                                        <td className="text-description py-0 my-0">
                                          {b?.totalDiscount ? `-${b?.totalDiscount.toLocaleString()}${t("Quotation.LinkPage.JPY")}` : `0${t("Quotation.LinkPage.JPY")}`}
                                        </td>
                                      </tr>
                                    ) : (
                                      b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.amount > 0 && (
                                        <tr
                                          key={i}
                                          className={`border-top-dashed`}
                                        >
                                          <td></td>
                                          <td><p className="tw:!font-hanken-grotesk px-4 py-0 mb-0 text-description tw:!text-[#4E4E4E]">{b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.id && b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.id === "lateNightFee" ? t(`Quotation.LinkPage.Table.${b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.id}`, { hour: b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.nightHours || 0 }) : b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.id ? t(`Quotation.LinkPage.Table.${b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.id}`) : b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.name}</p></td>
                                          <td></td>
                                          <td></td>
                                          <td className="text-description tw:!text-[#4E4E4E]">{`${b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.amount?.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</td>
                                        </tr>
                                      )
                                    )
                                  ) : (
                                    (bookingItemsDetailOrder[i] !== "discount" && b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.amount > 0) && (
                                      <tr
                                        key={i}
                                        className={`border-top-dashed`}
                                      >
                                        <td></td>
                                        <td><p className="tw:!font-hanken-grotesk px-4 py-0 mb-0 text-description tw:!text-[#4E4E4E]">{b?.details?.find(item => item.id === bookingItemsDetailOrder[i]).id && b?.details?.find(item => item.id === bookingItemsDetailOrder[i]).id === "lateNightFee" ? t(`Quotation.LinkPage.Table.${b?.details?.find(item => item.id === bookingItemsDetailOrder[i]).id}`, { hour: b?.details?.find(item => item.id === bookingItemsDetailOrder[i]).nightHours || 0 }) : b?.details?.find(item => item.id === bookingItemsDetailOrder[i]).id ? t(`Quotation.LinkPage.Table.${b?.details?.find(item => item.id === bookingItemsDetailOrder[i]).id}`) : b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.name}</p></td>
                                        <td></td>
                                        <td></td>
                                        <td className="text-description tw:!text-[#4E4E4E]">{`${b?.details?.find(item => item.id === bookingItemsDetailOrder[i])?.amount?.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</td>
                                      </tr>
                                    )
                                  )
                                ))
                              )}
                            </>
                          ))}

                          {getAdditionalGroups(item).map((group, groupIndex) => (
                            <React.Fragment key={`group-${groupIndex}`}>
                              {group.heading && (
                                <tr className={`border-top tw:!bg-[#F1F1F1]`}>
                                  <td colSpan={5}>
                                    <p className="tw:!font-hanken-grotesk m-0 py-0 tw:!text-[#212529]">{group.heading}</p>
                                  </td>
                                </tr>
                              )}
                              {(group.items || []).map((additionalItem, additionalIndex) => {
                                const itemName = additionalItem?.name || "-";
                                const additionalItemServiceDate = additionalItem?.serviceDate ? moment(additionalItem.serviceDate).format(formatMomentDate[lang]) : "";
                                const isFirstRow = !group.heading && additionalIndex === 0;
                                return (
                                  <tr className={isFirstRow ? "border-top" : "border-top-dashed"} key={`additional-${groupIndex}-${additionalIndex}`}>
                                    <td><p className="tw:!font-hanken-grotesk py-0 mb-0 tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{additionalItemServiceDate}</p></td>
                                    <td><p className="tw:!font-hanken-grotesk py-0 mb-0 tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{itemName}</p></td>
                                    <td className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{`${Number(additionalItem?.price || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</td>
                                    <td className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{additionalItem?.qty || ""}</td>
                                    <td style={{ whiteSpace: "nowrap", width: "1%" }}>
                                      <div className="d-flex align-items-center justify-content-start p-0">
                                        <span className="tw:!text-[#4E4E4E] tw:!font-[400] tw:!text-[14px]">{`${Number(additionalItem?.totalPrice || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          ))}

                          {/* Subtotal */}
                          <tr className="bg-DADADA">
                            <td colSpan={4} >
                              <div className="d-flex justify-content-end align-items-center p-0">
                                <span>{t("Quotation.LinkPage.Table.Subtotal")}</span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex justify-content-start align-items-center p-0">
                                <b className="tw:!font-hanken-grotesk m-0 p-0 text-nowrap">
                                  {item?.bookingItems
                                    ? `${(
                                      (item?.bookingItems || []).reduce(
                                        (subtotal, b) => subtotal + (b.discountedPrice ? b.discountedPrice : b.totalPrice),
                                        0
                                      ) +
                                      getAllAdditionalItems(item).reduce(
                                        (subtotal, additionalItem) => subtotal + Number(additionalItem?.totalPrice || 0),
                                        0
                                      )
                                    ).toLocaleString()}${t("Quotation.LinkPage.JPY")}`
                                    : `0${t("Quotation.LinkPage.JPY")}`}
                                </b>
                              </div>
                            </td>
                          </tr>

                          {
                            item?.message?.length > 0 && (
                              <tr className="bg-gold-grey-F9F9F9 border border-bottom">
                                <td className="m-2 d-flex justify-content-start align-items-start">
                                  <p className="tw:!font-hanken-grotesk py-0 mb-0 px-0 tw:!text-[14px]">
                                    {t("Quotation.LinkPage.TransferNotes")}
                                  </p>
                                </td>
                                <td colSpan={4} className="">
                                  {item?.message ? (
                                    item.message.map((m, noteIndex) => <p key={noteIndex} className="tw:!font-hanken-grotesk m-0 tw:!text-[14px]">{m.note}</p>)
                                  ) : (
                                    <p className="tw:!font-hanken-grotesk m-0 tw:!text-[14px]">-</p>
                                  )}
                                </td>
                              </tr>
                            )
                          }
                        </>
                      ))}

                      {/* Standalone Additional Items (desktop) */}
                      {quotationDetail?.standaloneItemsGroups?.length > 0 && (
                        <>
                          <tr className="bg-7C7B7B">
                            <td colSpan={5}>
                              <p className="tw:!font-hanken-grotesk m-0 tw:!text-[#FFFFFF] tw:!text-[14px] tw:!font-[400]">
                                {t("Quotation.LinkPage.AdditionalItems") || "Additional Items"}
                              </p>
                            </td>
                          </tr>
                          {quotationDetail.standaloneItemsGroups.map((group, groupIndex) => (
                            <React.Fragment key={`standalone-${groupIndex}`}>
                              {group.heading && (
                                <tr className="border-top tw:!bg-[#F1F1F1]">
                                  <td colSpan={5}><p className="tw:!font-hanken-grotesk m-0 py-0 tw:!text-[#212529]">{group.heading}</p></td>
                                </tr>
                              )}
                              {(group.items || []).map((item, itemIndex) => {
                                const isFirstRow = !group.heading && itemIndex === 0;
                                const serviceDate = item?.serviceDate ? moment(item.serviceDate).format(formatMomentDate[lang]) : "";
                                return (
                                  <tr className={isFirstRow ? "border-top" : "border-top-dashed"} key={`standalone-item-${groupIndex}-${itemIndex}`}>
                                    <td><p className="tw:!font-hanken-grotesk py-0 mb-0 tw:!text-[#4E4E4E] tw:!text-[14px]">{serviceDate}</p></td>
                                    <td><p className="tw:!font-hanken-grotesk py-0 mb-0 tw:!text-[#4E4E4E] tw:!text-[14px]">{item?.name || "-"}</p></td>
                                    <td className="tw:!text-[#4E4E4E] tw:!text-[14px]">{`${Number(item?.price || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</td>
                                    <td className="tw:!text-[#4E4E4E] tw:!text-[14px]">{item?.qty || ""}</td>
                                    <td className="tw:!text-[#4E4E4E] tw:!text-[14px]">{`${Number(item?.totalPrice || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          ))}
                          <tr className="bg-DADADA">
                            <td colSpan={4}>
                              <div className="d-flex justify-content-end align-items-center p-0">
                                <span>{t("Quotation.LinkPage.Table.Subtotal")}</span>
                              </div>
                            </td>
                            <td>
                              <b className="tw:!font-hanken-grotesk m-0 p-0 text-nowrap">
                                {`${computeStandaloneItemsTotal(quotationDetail.standaloneItemsGroups).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                              </b>
                            </td>
                          </tr>
                        </>
                      )}

                      {/* Total */}
                      <tr className="bg-dark text-white">
                        <td colSpan={4} >
                          <div className="d-flex justify-content-end align-items-center p-0">
                            <p className="tw:!font-hanken-grotesk py-0 my-0 text-white fs-12">
                              <b className="tw:!font-hanken-grotesk fs-16">{t("Quotation.LinkPage.Table.Total")}</b> {" "}
                              {t("Quotation.LinkPage.Table.TaxInclude")}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex justify-content-start align-items-center p-0">
                            <b className="tw:!font-hanken-grotesk m-0 p-0 fs-18 text-nowrap">
                              {`${computeQuotationTotalAmount(quotationDetail).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                            </b>
                          </div>
                        </td>
                      </tr>
                    </>
                  </table>
                </div>

                {/* MOBILE VIEW */}
                <div className="d-block d-md-none">
                  <div className="d-flex bg-dark justify-content-between align-items-center p-3">
                    <div className="">
                      <b className="tw:!font-hanken-grotesk pb-0  text-white">{t("Quotation.LinkPage.Table.Name")}</b>
                      <p className="tw:!font-hanken-grotesk py-0  mb-0 text-white">{t("Quotation.LinkPage.Table.Description")}</p>
                    </div>
                    <p className="tw:!font-hanken-grotesk mb-0 text-white">{t("Quotation.LinkPage.Table.Amount")}</p>
                  </div>
                  {quotationDetail?.bookings.map((item, index) => (
                    <>
                      <div className="bg-7C7B7B p-3">
                        <p className="tw:!font-hanken-grotesk m-0 text-white">
                          {t(`BookingType.${getBookingType(item?.bookingType, item?.isAirportTransfer)}`)}
                        </p>
                      </div>
                      {item?.bookingItems.map((b, bIndex) => {
                        return (
                          <div className="p-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="">
                                <b className="tw:!font-hanken-grotesk">
                                  {
                                    lang === "JP" ? b?.nameJP || b?.name : b?.name
                                  }
                                </b>
                                <p className="tw:!font-hanken-grotesk py-0 mb-0">
                                  {
                                    lang === "JP" ? b?.descJP || b?.desc : b?.desc
                                  }
                                </p>
                              </div>
                              {!openItems.includes(index + "" + bIndex) && (
                                <p className="tw:!font-hanken-grotesk m-0 p-0 text-nowrap">
                                  {`${item.totalPrice.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                                </p>
                              )}
                            </div>
                            {openItems.includes(index + "" + bIndex) && (
                              <>
                                <div className="border-top mt-2">
                                  <div className="py-2">
                                    <div className="d-flex justify-content-between">
                                      <p className="tw:!font-hanken-grotesk m-0 fs-13">{t("Quotation.LinkPage.Table.ServiceDate")}</p>
                                      <p className="tw:!font-hanken-grotesk m-0 fs-13">
                                        {
                                          lang === "JP" ? b?.serviceDateJP || b?.serviceDate : b?.serviceDate
                                        }
                                      </p>
                                    </div>
                                    <div className="d-flex justify-content-between mt-2">
                                      <p className="tw:!font-hanken-grotesk m-0 fs-13">{t("Quotation.LinkPage.Table.UnitPrice")}</p>
                                      <p className="tw:!font-hanken-grotesk m-0 fs-13">
                                        {`${b?.price.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                                      </p>
                                    </div>
                                    <div className="d-flex justify-content-between mt-2">
                                      <p className="tw:!font-hanken-grotesk m-0 fs-13">{t("Quotation.LinkPage.Table.Qty")}</p>
                                      <p className="tw:!font-hanken-grotesk m-0 fs-13">{b?.qty}</p>
                                    </div>
                                  </div>
                                </div>
                                {
                                  b?.type === "vehicle" && (
                                    <div className="border-top">
                                      <div className="py-2">
                                        {
                                          b?.details?.map((v, i) => (
                                            v?.amount > 0 && (
                                              <div className={`d-flex justify-content-between ${i > 0 && "mt-2"}`}>
                                                <p className="tw:!font-hanken-grotesk m-0 fs-13">{v?.id && v?.id === "lateNightFee" ? t(`Quotation.LinkPage.Table.${v?.id}`, { hour: v?.nightHours || 0 }) : v?.id ? t(`Quotation.LinkPage.Table.${v?.id}`) : v?.name}</p>
                                                <p className="tw:!font-hanken-grotesk m-0 fs-13">
                                                  {`${v?.amount?.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                                                </p>
                                              </div>
                                            )
                                          ))
                                        }
                                      </div>
                                    </div>
                                  )
                                }
                                <div className="mt-2 border-top">
                                  <div className="py-2">
                                    <div className="d-flex justify-content-between">
                                      <b className="tw:!font-hanken-grotesk">{t("Quotation.LinkPage.Table.Amount")}</b>
                                      <b className="tw:!font-hanken-grotesk">{`${(
                                        b?.price * b?.qty
                                      )?.toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</b>
                                    </div>
                                  </div>
                                </div>
                                {
                                  b.type === "vehicle" && item.useDiscount && (
                                    <div className="mt-2 border-top">
                                      <div className="py-2">
                                        <div className="d-flex justify-content-between">
                                          <p className="tw:!font-hanken-grotesk m-0 fs-13">{`${t("Quotation.LinkPage.Table.Discount")} (${Number(b?.discount || 0)}%)`}</p>
                                          <p className="tw:!font-hanken-grotesk m-0 fs-13">
                                            {b?.totalDiscount ? `-${b?.totalDiscount?.toLocaleString()}${t("Quotation.LinkPage.JPY")}` : `0${t("Quotation.LinkPage.JPY")}`}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                }
                              </>
                            )}
                            <div className="text-center">
                              {openItems.includes(index + bIndex) ? (
                                <CollapseUpIcon
                                  onClick={() => toggleOpen(index + "" + bIndex)}
                                />
                              ) : (
                                <CollapseDownIcon
                                  onClick={() => toggleOpen(index + "" + bIndex)}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div className="bg-DCDDDE p-3 ">
                        <div className="d-flex justify-content-between">
                          <p className="tw:!font-hanken-grotesk m-0 fs-14">{t("Quotation.LinkPage.Table.Subtotal")}</p>
                          <b className="tw:!font-hanken-grotesk m-0 p-0">
                            {item?.bookingItems
                              ? `${item.bookingItems.reduce(
                                (subtotal, b) => subtotal + (b.discountedPrice ? b.discountedPrice : b.totalPrice), 0
                              ).toLocaleString()}${t("Quotation.LinkPage.JPY")}`
                              : `0${t("Quotation.LinkPage.JPY")}`}
                          </b>
                        </div>
                      </div>
                      {
                        item?.message?.length > 0 && (
                          <>
                            <div className="bg-gold-grey-F9F9F9 p-3 border-bottom">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="">
                                  <b className='tw:!font-hanken-grotesk tw:!text-[15px]'>
                                    {t("Quotation.LinkPage.TransferNotes")}
                                  </b>
                                  <p className="tw:!font-hanken-grotesk pt-3 mb-0">
                                    {item?.message ? (
                                      item.message.map((m, noteIndex) => <p key={noteIndex} className="tw:!font-hanken-grotesk m-0 tw:!text-[14px]">{m.note}</p>)
                                    ) : (
                                      <p className="tw:!font-hanken-grotesk m-0 tw:!text-[14px]">-</p>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </>
                        )
                      }
                    </>
                  ))}
                  {/* Standalone Additional Items (mobile) */}
                  {quotationDetail?.standaloneItemsGroups?.length > 0 && (
                    <>
                      <div className="bg-7C7B7B p-3">
                        <p className="tw:!font-hanken-grotesk m-0 text-white">{t("Quotation.LinkPage.AdditionalItems") || "Additional Items"}</p>
                      </div>
                      {quotationDetail.standaloneItemsGroups.map((group, groupIndex) => (
                        <div className="p-3 border-bottom" key={`mobile-standalone-${groupIndex}`}>
                          {group.heading && <div className="d-flex justify-content-between"><b className="tw:!font-hanken-grotesk">{group.heading}</b></div>}
                          <div className={group.heading ? "border-top mt-2" : ""}>
                            {(group.items || []).map((item, itemIndex) => {
                              const serviceDate = item?.serviceDate ? moment(item.serviceDate).locale("en").format("MMMM DD, YYYY") : "-";
                              return (
                                <div key={itemIndex} className={`d-flex justify-content-between align-items-start ${itemIndex > 0 ? "mt-2" : "pt-2"}`}>
                                  <div>
                                    <p className="tw:!font-hanken-grotesk py-0 mb-0 text-description">{item?.name || "-"}</p>
                                    <p className="tw:!font-hanken-grotesk py-0 mb-0 fs-13 text-7C7B7B">{serviceDate}</p>
                                    <p className="tw:!font-hanken-grotesk py-0 mb-0 fs-13 text-7C7B7B">{`${t("Quotation.LinkPage.Table.Qty")}: ${item?.qty || ""}`}</p>
                                  </div>
                                  <p className="tw:!font-hanken-grotesk m-0 p-0 text-nowrap text-description">{`${Number(item?.totalPrice || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <div className="bg-DCDDDE p-3">
                        <div className="d-flex justify-content-between">
                          <p className="tw:!font-hanken-grotesk m-0 fs-14">{t("Quotation.LinkPage.Table.Subtotal")}</p>
                          <b className="tw:!font-hanken-grotesk">{`${computeStandaloneItemsTotal(quotationDetail.standaloneItemsGroups).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</b>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="bg-dark text-white p-3 ">
                    <div className="d-flex justify-content-between">
                      <p className="tw:!font-hanken-grotesk py-0 my-0 text-white fs-10">
                        <b className="tw:!font-hanken-grotesk fs-14">{t("Quotation.LinkPage.Table.Total")}</b> {" "}
                        {t("Quotation.LinkPage.Table.TaxInclude")}
                      </p>
                      <b className="tw:!font-hanken-grotesk m-0 p-0">
                        {`${computeQuotationTotalAmount(quotationDetail).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                      </b>
                    </div>
                  </div>
                </div>
                {quotationDetail?.notes && (
                  <div className="p-3">
                    <p className="tw:!font-hanken-grotesk noto mx-0 mt-0 mb-1" style={{ fontWeight: "700" }}>
                      {t("Quotation.LinkPage.QuotationNotes")}
                    </p>
                    <p className="tw:!font-hanken-grotesk">
                      {quotationDetail?.notes}
                    </p>
                  </div>
                )}

              </div>
            ) : (
              <PDFViewer width={"100%"} height={"1000"}>
                <QuotationDocument data={quotationDetail} />
              </PDFViewer>
            )}
          </div>
        </main>
      </Layout>
    </>
  );
};

export default QuotationDownloadPage;
